<template>
  <div
    class="py-4 container-fluid my-inga-page"
    :class="{ 'my-inga-dark': isDarkMode }"
  >
    <div class="row">
      <div class="col-12 col-lg-8 mb-4">
        <div v-if="myGames.ChooseReward.length > 0" class="card mb-3">
          <div class="card-header pb-0">
            <h5>Výběr výhry</h5>
          </div>
          <div class="card-body px-2 pt-2 pb-0">
            <choose-reward-table :rows="myGames.ChooseReward" @choose-reward-data="getChooseRewardData" />
          </div>
        </div>
        <div v-if="myGames.RecomendedGame.length > 0" class="card mb-3">
          <div class="card-header pb-0">
            <h5>Doporučené hry</h5>
          </div>
          <div class="card-body px-2 pt-2 pb-0">
            <recomended-game-table :rows="myGames.RecomendedGame" />
          </div>
        </div>
        <div v-if="myGames.RunningGame.length > 0" class="card mb-3">
          <div class="card-header pb-0">
            <h5 class="card-section-title">Běžící hry</h5>
          </div>
          <div class="card-body p-3">
            <div
              v-for="(game, index) in myGames.RunningGame"
              :key="game.GameID"
              class="card running-my-card"
              :class="{ 'mb-3': index < myGames.RunningGame.length - 1 }"
              role="link"
              tabindex="0"
              @click="goToRunningGame(game)"
              @keydown.enter.prevent="goToRunningGame(game)"
            >
              <div class="card-body p-3">
                <h4 class="running-my-title mb-3">{{ game.GameName }}</h4>
                <div class="row g-3">
                  <div class="col-12 col-xl-6">
                    <div class="card h-100 split-table-card">
                      <div class="card-header pb-0">
                        <h6 class="mb-0">Moje portfolio</h6>
                      </div>
                      <div class="card-body px-2 pt-2 pb-0">
                        <running-game-table v-if="game.Portfolio != undefined" :rows="game.Portfolio" />
                        <h6 class="text-center" v-else>V tuto chvíli nejsou dostupná žádná herní data</h6>
                      </div>
                    </div>
                  </div>
                  <div class="col-12 col-xl-6">
                    <div class="card h-100 split-table-card">
                      <div class="card-header pb-0">
                        <h6 class="mb-0">Průběžné výsledky hry</h6>
                      </div>
                      <div class="card-body px-2 pt-2 pb-0">
                        <div v-if="game.Result != undefined" class="keep-table-footer">
                          <game-result-table :rows="game.Result" :searchable="false" />
                        </div>
                        <h6 class="text-center" v-else>V tuto chvíli nejsou dostupné žádné výsledky</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="myGames.WaitingGame.length > 0" class="card mb-3">
          <div class="card-header pb-0">
            <h5>Čekající hry</h5>
          </div>
          <div class="card-body px-2 pt-2 pb-0">
            <waiting-game-table :rows="myGames.WaitingGame" />
          </div>
        </div>
        <div v-if="myGames.FinishedGame.length > 0" class="card mb-3">
          <div class="card-header pb-0">
            <h5>Ukončené hry</h5>
          </div>
          <div class="card-body px-2 pt-2 pb-0">
            <finished-game-table :rows="myGames.FinishedGame" />
          </div>
        </div>
        <div v-if="myGames.ObsoleteGame.length > 0" class="card">
          <div class="card-header pb-0">
            <h5>Neproběhlé hry</h5>
          </div>
          <div class="card-body px-2 pt-2 pb-0">
            <obsolete-game-table :rows="myGames.ObsoleteGame" />
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-4 mb-4">
        <profile-card class="mb-4" :secured="false" :user="user" />
        <ActiveGamesSidebar />
      </div>
    </div>
    <base-modal v-if="store.state.modal.isOpen && store.state.modal.view === 'WithdrawReward'">
      <withdraw-reward-card
        :reward-data="rewardData"
        :game-id="gameID"
        @withdraw-reward-success="getMyGames"
      />
    </base-modal>
  </div>
</template>

<script setup>
import { ref, reactive, computed, inject, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import dataService from "@/services/dataService.js";
import ChooseRewardTable from "./components/custom/tables/ChooseRewardTable.vue";
import RecomendedGameTable from "./components/custom/tables/RecomendedGameTable.vue";
import RunningGameTable from "./components/custom/tables/RunningGameTable.vue";
import GameResultTable from "./components/custom/tables/GameResultTable.vue";
import WaitingGameTable from "./components/custom/tables/WaitingGameTable.vue";
import FinishedGameTable from "./components/custom/tables/FinishedGameTable.vue";
import ObsoleteGameTable from "./components/custom/tables/ObsoleteGameTable.vue";
import ProfileCard from "./components/custom/ProfileCard.vue";
import ActiveGamesSidebar from "./components/custom/ActiveGamesSidebar.vue";
import BaseModal from "@/components/BaseModal.vue";
import WithdrawRewardCard from "./components/custom/WithdrawRewardCard.vue";

const store = useStore();
const router = useRouter();
const swal = inject('$swal');
const isDarkMode = computed(() => !!store.state.isDarkMode);

const myGames = reactive({
  ChooseReward: [],
  RecomendedGame: [],
  RunningGame: [],
  WaitingGame: [],
  FinishedGame: [],
  ObsoleteGame: [],
});
const rewardData = ref(null)
const gameID = ref(null);

const user = computed(() => store.getters.getUser);
const userID = computed(() => store.getters.getUser?.UserID);
const sessionHash = computed(() => store.getters.isLoggedIn);

const getMyGames = async () => {
  const response = await dataService.GetMyGame(userID.value, sessionHash.value);
  if (!response) return;

  if (response.ChooseReward?.length > 0) myGames.ChooseReward = response.ChooseReward;
  myGames.RecomendedGame = response.RecomendedGame || [];
  myGames.RunningGame = response.RunningGame || [];
  myGames.WaitingGame = response.WaitingGame || [];
  myGames.FinishedGame = response.FinishedGame || [];
  myGames.ObsoleteGame = response.ObsoleteGame || [];
}

const goToRunningGame = (game) => {
  if (!game?.GameID) return;
  router.push({ name: 'Běžící hra', params: { gid: game.GameID } });
};

const getChooseRewardData = async (GameID) => {
  const response = await dataService.GetChooseRewardData(userID.value, sessionHash.value, GameID);

  if (response.Result) {
    gameID.value = GameID;
    rewardData.value = response.Data;
    store.dispatch('openModal', {
      title: 'Výběr výhry',
      view: 'WithdrawReward'
    });
  } else {
    swal.fire({
      position: 'center',
      icon: 'error',
      html: `${response.ErrorMessage.map(message => `<h5>${message}</h5>`).join('')}`,
      showConfirmButton: false,
      timer: 3000,
    })
  }
};

getMyGames();

watch(() => store.state.gamesRefreshKey, () => {
  if (userID.value && sessionHash.value) getMyGames();
});

</script>

<style scoped>
.my-inga-page :deep(.vgt-table),
.my-inga-page :deep(.vgt-table.vgt-flush) {
  border: none !important;
}

.my-inga-page :deep(.vgt-table tbody tr:last-child td) {
  border-bottom: none !important;
}

.my-inga-page :deep(.vgt-wrap__footer),
.my-inga-page :deep(.vgt-wrap__actions-footer) {
  display: none !important;
  height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  overflow: hidden !important;
}

.my-inga-page .keep-table-footer :deep(.vgt-wrap__footer),
.my-inga-page .keep-table-footer :deep(.vgt-wrap__actions-footer) {
  display: block !important;
  height: auto !important;
  padding: inherit !important;
  margin: inherit !important;
  overflow: visible !important;
}

.my-inga-page :deep(.vgt-responsive) {
  margin-bottom: 0 !important;
}

.my-inga-page :deep(.vgt-table td) {
  padding-top: 0.45rem !important;
  padding-bottom: 0.45rem !important;
}

.split-table-card {
  border: 1px solid #E2E8F0;
  box-shadow: none;
}

.running-my-card {
  cursor: pointer;
  box-shadow: none;
  border: 1px solid #E2E8F0;
}

.running-my-card:hover {
  border-color: #FF8A4C;
}

.running-my-title {
  font-family: 'Ubuntu Sans', Ubuntu, sans-serif;
  font-weight: 700;
  font-size: 1.2rem;
  line-height: 1.3;
  color: #FF8A4C;
  word-break: break-word;
}

@media (max-width: 430px) {
  .running-my-card .card-body {
    padding: 0.85rem !important;
  }

  .running-my-title {
    font-size: 1.08rem;
  }
}

.my-inga-dark .running-my-title {
  color: #FF8A4C !important;
}

.my-inga-dark .running-my-card {
  border-color: #334155;
}

.my-inga-dark .running-my-card:hover {
  border-color: #FF8A4C;
}

.my-inga-dark {
  min-height: 100vh;
  background: #1E293B !important;
  color: #E2E8F0;
}

.my-inga-dark :deep(.card) {
  background: #0F172A !important;
  background-color: #0F172A !important;
  box-shadow: none !important;
  border: 1px solid #334155 !important;
  color: #E2E8F0 !important;
}

.my-inga-dark :deep(.card-header),
.my-inga-dark :deep(.card-body) {
  background: transparent !important;
  color: #E2E8F0 !important;
}

.my-inga-dark :deep(h5),
.my-inga-dark :deep(h6),
.my-inga-dark :deep(.text-dark),
.my-inga-dark :deep(.text-navy-blue),
.my-inga-dark :deep(.card-header h5),
.my-inga-dark :deep(.card-section-title) {
  color: #E2E8F0 !important;
}

.my-inga-dark :deep(.text-primary) {
  color: #FF8A4C !important;
}

.my-inga-dark .section-head-dark {
  background: #1E293B !important;
  border: 1px solid #334155;
}

.my-inga-dark :deep(.vgt-inner-wrap),
.my-inga-dark :deep(.vgt-responsive),
.my-inga-dark :deep(.vgt-wrap) {
  background: transparent !important;
  box-shadow: none !important;
}

.my-inga-dark :deep(.vgt-table) {
  background: transparent !important;
  color: #E2E8F0 !important;
  border: none !important;
}

.my-inga-dark :deep(.vgt-table th) {
  background: #1E293B !important;
  color: #94A3B8 !important;
  border-color: #334155 !important;
}

.my-inga-dark :deep(.vgt-table td) {
  background: transparent !important;
  color: #E2E8F0 !important;
  border-color: #334155 !important;
}

.my-inga-dark :deep(.vgt-table tbody tr:hover),
.my-inga-dark :deep(.vgt-table tr.clickable:hover) {
  background-color: #1E293B !important;
}

.my-inga-dark :deep(.vgt-wrap__footer) {
  background: #1E293B !important;
  color: #94A3B8 !important;
  border: none !important;
}

.my-inga-dark :deep(.vgt-table th.sortable button),
.my-inga-dark :deep(.vgt-table th.sortable span) {
  color: #94A3B8 !important;
}

.my-inga-dark :deep(.vgt-global-search) {
  background: transparent !important;
  border: none !important;
  padding: 0 0 12px !important;
}

.my-inga-dark :deep(.vgt-input),
.my-inga-dark :deep(.vgt-select) {
  background: #1E293B !important;
  color: #E2E8F0 !important;
  border: 1px solid #334155 !important;
}

.my-inga-dark :deep(.vgt-row-me) {
  background: rgba(255, 138, 76, 0.08) !important;
}

.my-inga-dark :deep(.vgt-row-active td) {
  background: rgba(91, 130, 172, 0.12) !important;
}
</style>
